import React, { useState, useEffect } from 'react';
import {
  Typography,
  Skeleton,
  Layout,
  Descriptions,
  Divider,
  Checkbox,
  Form,
  Modal,
  Alert,
  Popconfirm,
  Card,
  Input,
} from 'antd';
import dayjs from 'dayjs';
import {
  catchExceptionSRate,
  getLocalStorage,
  IsRole,
  useQuery,
} from '../../utils/helpers/helpers';
import BreadcrumbContainer from '../../containers/Breadcrumb';
import INTERNAL_LINKS from '../../enums/InternalLinks';

import {
  applyToRole,
  get,
  getAnexoUrl,
  reject,
  getRoles,
} from '../../api/module/announcement_contractor';
import { useHistory, useParams } from 'react-router-dom';
import { CustomButton } from '../../components';
import SpinLoad from '../../components/SpinLoad';

const { Title, Paragraph } = Typography;
const { Content } = Layout;

const breadcrumbItemsHistory = [
  {
    text: 'Convocatoria',
    to: `${INTERNAL_LINKS.ANNOUNCEMENT}`,
  },
  {
    text: 'Histórico Convocatorias',
    to: `${INTERNAL_LINKS.ANNOUNCEMENT_HISTORY}`,
  },
  {
    text: 'Previsualización Convocatoria',
  },
];

const periodOptions = {
  1: 'Mensual',
  2: 'Total contrato',
  3: 'Entrega de productos',
  4: 'Otro',
};

const ApplicationForm = ({ cargo, has_education, afterApplication }) => {
  const navigate = useHistory();
  const [loadingApply, setLoadingApply] = useState(false);
  const [has_applied, setApplied] = useState(cargo.aplicacion);

  const [form] = Form.useForm();

  const sendAplication = async (formData) => {
    const personType = getLocalStorage('person_type');
    const profileLink =
      personType === '1'
        ? `${INTERNAL_LINKS.NATURAL_PERSON}/${getLocalStorage('tercero')}`
        : `${INTERNAL_LINKS.LEGAL_PERSON}/${getLocalStorage('tercero')}`;

    if (!has_education && personType === '1') {
      Modal.info({
        title: 'Información importante',
        content:
          'Para poder aplicar a esta convocatoria primero complete la información/documentación de su perfil y luego regrese aquí para aplicar',
        okText: 'Ir a actualizar',
        onOk: () => navigate.push(profileLink),
      });
      return;
    }

    setLoadingApply(true);
    try {
      await applyToRole(cargo.id, formData);
      setApplied(true);
      afterApplication();
      Modal.confirm({
        title: 'Información importante',
        content:
          'Recuerde actualizar la información de educación y experiencia laboral en su perfil de SIGA',
        okText: 'Ir a actualizar',
        cancelText: 'Cerrar',
        onOk: () => navigate.push(profileLink),
        onClose: () => window.location.reload(),
      });
    } catch (err) {
      try {
        if (err.response.data.errors[0].code === 'unique') {
          Modal.error({
            title: 'Usted ya ha aplicado a este cargo previamente',
            content: (
              <>
                <Divider />
                <Paragraph>
                  Recuerde completar la información de registro por la opción
                  "Perfil".
                </Paragraph>
                <Paragraph>
                  Nos estaremos comunicando con usted por medio del correo
                  electrónico registrado. (Revise las bandejas de entrada de su
                  correo electrónico, incluyendo notificaciones, publicidad y
                  correo no deseado (spam))
                </Paragraph>
                <Paragraph>Gracias</Paragraph>
              </>
            ),
          });
          setApplied(true);
        } else throw err;
      } catch (err) {
        catchExceptionSRate(err);
      }
    } finally {
      setLoadingApply(false);
    }
  };

  if (has_applied)
    return (
      <>
        <br />
        <Descriptions
          title={<Title level={4}>Aceptar lugar y cargo - {cargo.cargo}</Title>}
          bordered
          column={1}
        >
          <Descriptions.Item label="Aceptar y aplicar a cargo/rol">
            <Alert
              message="Usted ya ha aplicado a este cargo"
              description='Recuerde completar la información de registro por la opción "Perfil", incluyendo los documentos requeridos en la convocatoria. Por favor revise constantemente las bandejas de entrada de su correo electrónico, incluyendo notificaciones, publicidad y correo no deseado (spam).'
              type="info"
              showIcon
            />
          </Descriptions.Item>
        </Descriptions>
      </>
    );

  return (
    <Form scrollToFirstError form={form} onFinish={sendAplication}>
      <br />
      <Descriptions
        title={<Title level={4}>Aceptar lugar y cargo - {cargo.cargo}</Title>}
        bordered
        column={1}
      >
        {cargo.lugares.length > 0 ? (
          <Descriptions.Item label="A qué lugar(es) desea aplicar">
            <Form.Item
              name="lugares"
              rules={[
                { required: true, message: 'Debe elegir al menos un lugar' },
              ]}
            >
              <Checkbox.Group
                options={cargo.lugares.map((v) => ({
                  value: v[0],
                  label: v[1],
                }))}
                direction="horizontal"
              />
            </Form.Item>
          </Descriptions.Item>
        ) : null}

        <Descriptions.Item label={<b>IMPORTANTE</b>}>
          <b>
            En caso de aplicar a un lugar diferente a donde reside actualmente,
            el proyecto no asumirá los costos y/o gastos en los que incurra por
            el traslado y manutención
          </b>
        </Descriptions.Item>
        <Descriptions.Item label="Aceptar y aplicar a cargo/rol">
          <Popconfirm
            onConfirm={() => {
              form.submit();
            }}
            title="Tenga en cuenta que usted está aceptando los requisitos de la presente convocatoria al aceptarla"
            okText="Acepto"
            cancelText="No Acepto"
          >
            <CustomButton
              loading={loadingApply}
              htmlType="button"
              text="Aceptar y aplicar a este cargo/rol"
              disabled={has_applied}
            />
          </Popconfirm>
        </Descriptions.Item>
      </Descriptions>
    </Form>
  );
};

const AnnouncementShow = ({ history }) => {
  const { announcement_id } = useParams();
  const [initialValues, setInitialValues] = useState(null);
  const [initialValuesCargoRol, setInitialValuesCargoRol] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);

  const [superUser] = useState(IsRole(6));

  let query = useQuery();
  const isHistory = query.get('history') === 'true';
  const isPreview = query.get('preview') === 'true';

  const [hasApplied, setHasApplied] = useState(false);

  const [isRejected, setRejected] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [formReject] = Form.useForm();

  const breadcrumbItems = [
    {
      text: 'Convocatoria',
      to: `${INTERNAL_LINKS.ANNOUNCEMENT}`,
    },
    {
      text: 'Editar Convocatoria',
      to: `${INTERNAL_LINKS.ANNOUNCEMENT_FORM}/${announcement_id}`,
    },
    {
      text: 'Previsualización Convocatoria',
    },
  ];

  useEffect(() => {
    const handleGet = async () => {
      try {
        const { data: responseData } = await get(
          announcement_id,
          isHistory || isPreview,
        );
        const responseRoles = await getRoles(
          '?filter[convocatoria]=' + announcement_id,
          isHistory || isPreview,
        );
        setRejected(
          responseData.estado_aplicacion === 'Convocatoria rechazada',
        );
        setHasApplied(responseRoles.data.some((r) => r.aplicacion));
        setInitialValuesCargoRol(responseRoles.data);
        setInitialValues({
          ...responseData,
          fecha_inicio: dayjs(responseData.fecha_inicio),
          fecha_fin: dayjs(responseData.fecha_fin),
        });
      } catch (err) {
        catchExceptionSRate(err);
      }
    };
    if (announcement_id) handleGet();
  }, [announcement_id]);

  const showDocument = async () => {
    setLoadingDocument(true);
    const { data } = await getAnexoUrl(
      initialValues.id,
      isHistory || isPreview,
    );

    window.open(data.url);

    setLoadingDocument(false);
  };

  const rejectAnnouncement = async (data) => {
    try {
      setRejected(true);
      setLoadingReject(true);
      await reject(initialValues.id, data);
      window.location.reload();
    } catch (err) {
      setRejected(false);
      catchExceptionSRate(err);
    } finally {
      setLoadingReject(false);
    }
  };

  const back = () => {
    history.goBack();
  };

  return (
    <Content>
      {isHistory ? (
        <BreadcrumbContainer items={breadcrumbItemsHistory} />
      ) : isPreview ? (
        <BreadcrumbContainer items={breadcrumbItems} />
      ) : null}
      {isHistory || isPreview ? (
        <Title className={'text-uppercase'} level={2}>
          Previsualización de convocatoria
        </Title>
      ) : null}
      {!initialValues ? (
        <Skeleton loading active />
      ) : (
        <Card
          size={'small'}
          bordered={false}
          title={
            <Title level={3} style={{ whiteSpace: 'normal' }}>
              {initialValues.nombre}
            </Title>
          }
          extra={
            <img
              src={initialValues.organizacion_logo}
              alt="Logo Organización"
              style={{ width: 'calc(250px - 20px)' }}
            />
          }
        >
          <Descriptions
            className="description-label-style"
            title="Información de la convocatoria"
            bordered
            column={2}
          >
            <Descriptions.Item label="Organización" span={2}>
              {initialValues.organizacion_nombre}
            </Descriptions.Item>
            <Descriptions.Item
              label="Proyecto, licitación, concurso u otro"
              span={2}
            >
              {initialValues.nombre_proyecto}
            </Descriptions.Item>
            <Descriptions.Item label="Número de convocatoria">
              {initialValues.numero}
            </Descriptions.Item>
            <Descriptions.Item label="Archivo anexo de la convocatoria">
              {initialValues.tiene_anexo ? (
                <CustomButton
                  block
                  type="default"
                  text="Ver"
                  htmlType="button"
                  loading={loadingDocument}
                  onClick={showDocument}
                />
              ) : (
                'Sin adjunto'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Nombre de la convocatoria" span={2}>
              {initialValues.nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha inicio de la convocatoria">
              {initialValues.fecha_inicio.format('DD - MMMM - YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha fin de la convocatoria">
              {initialValues.fecha_fin.format('DD - MMMM - YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Descripción de la convocatoria" span={2}>
              {initialValues.descripcion.split('\n').map((value) => (
                <>
                  {value} <br />
                </>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="IMPORTANTE" span={2}>
              Esta convocatoria podrá ser cerrada/finalizada de forma anticipada
              una vez se cubran la(s) vacante(s) requerida(s).
            </Descriptions.Item>
          </Descriptions>
          {initialValuesCargoRol.map((item, index) => (
            <>
              <Divider>
                <Title level={5}>
                  Cargo / Rol No. {index + 1} - {item.cargo}
                </Title>
              </Divider>
              <Descriptions
                className="description-label-style"
                bordered
                column={3}
              >
                <Descriptions.Item label="Observaciones para el cargo" span={3}>
                  {(item.observaciones_cargo || '').split('\n').map((value) => (
                    <>
                      {value} <br />
                    </>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item
                  label={`Remuneración ${
                    item.remuneracion_variable != null ? 'variable' : 'fija'
                  }`}
                  span={3}
                >
                  {item.remuneracion_variable != null
                    ? item.remuneracion_variable.split('\n').map((value) => (
                        <>
                          {value} <br />
                        </>
                      ))
                    : item.remuneracion_fija != null
                      ? `$ ${item.remuneracion_fija}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ',',
                        )
                      : ''}
                </Descriptions.Item>
                <Descriptions.Item label="Período" span={3}>
                  {item.periodo === 4
                    ? item.periodo_cual
                    : periodOptions[item.periodo]}
                </Descriptions.Item>
                <Descriptions.Item label="Total vacantes" span={3}>
                  {item.total_vacantes}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo contrato" span={3}>
                  {item.tipo_contrato_nombre || 'Por definir'}
                </Descriptions.Item>
                <Descriptions.Item
                  label={`Duración del proyecto - ${item.unidad_tiempo_texto || ''}`}
                  span={3}
                >
                  {item.duracion}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Fecha estimada de inicio de labores"
                  span={3}
                >
                  {item.fecha_inicio
                    ? dayjs(item.fecha_inicio).format('DD - MMMM - YYYY')
                    : ''}
                </Descriptions.Item>
                <Descriptions.Item label="Estudios" span={3}>
                  {(item.estudios || '').split('\n').map((value) => (
                    <>
                      {value} <br />
                    </>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo de experiencia laboral" span={3}>
                  {(item.tipo_experiencia_laboral || '')
                    .split('\n')
                    .map((value) => (
                      <>
                        {value} <br />
                      </>
                    ))}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      Disponibilidad para viajar / desplazarse <br />
                      durante el proyecto
                    </>
                  }
                >
                  {item.disponibilidad_viajar ? 'Si' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      Posibilidad de radicarse en <br />
                      otra ciudad durante el proyecto
                    </>
                  }
                >
                  {item.radicarse_otra_ciudad ? 'Si' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      Tiempo de experiencia <br />
                      laboral (meses)
                    </>
                  }
                >
                  {item.tiempo_experiencia_laboral || ''}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Lugar de realización de labores"
                  span={3}
                >
                  {item.lugares.map((value) => (
                    <>
                      {value[1]} <br />
                    </>
                  ))}
                </Descriptions.Item>
              </Descriptions>
              {!isRejected && !superUser && !isPreview && !isHistory ? (
                <ApplicationForm
                  cargo={item}
                  has_education={initialValues.tiene_educacion}
                  afterApplication={() => {
                    setHasApplied(true);
                  }}
                />
              ) : null}
            </>
          ))}
          {!isPreview && !isHistory && !superUser && (
            <SpinLoad loading={isRejected === null}>
              {isRejected === false && !hasApplied && (
                <Form
                  form={formReject}
                  layout="vertical"
                  onFinish={rejectAnnouncement}
                >
                  <Descriptions
                    title={<Divider>Rechazar convocatoria</Divider>}
                    bordered
                  >
                    <Descriptions.Item label="Motivo de rechazo">
                      <Form.Item
                        name="motivo"
                        rules={[
                          {
                            required: true,
                            message: 'El motivo es obligatorio',
                          },
                        ]}
                      >
                        <Input.TextArea
                          placeholder={'Motivo de rechazo'}
                          rows={5}
                        />
                      </Form.Item>
                      <Form.Item>
                        <Popconfirm
                          title="Sr usuario(a), si usted rechaza esta convocatoria recuerde que no podrá aplicar a esta nuevamente"
                          onConfirm={() => {
                            formReject.submit();
                          }}
                          okText="Sí"
                        >
                          <CustomButton
                            loading={loadingReject}
                            text="Guardar"
                            htmlType="button"
                          />
                        </Popconfirm>
                      </Form.Item>
                    </Descriptions.Item>
                  </Descriptions>
                </Form>
              )}
              {isRejected && (
                <Alert
                  message="Atención"
                  description="La convocatoria ha sido rechazada."
                  type="warning"
                  showIcon
                />
              )}
            </SpinLoad>
          )}
          <CustomButton
            style={{ marginTop: 10 }}
            type={'default'}
            className={'back-button'}
            htmlType="button"
            onClick={() => back()}
            text="Volver"
            danger
          />
        </Card>
      )}
    </Content>
  );
};

export default AnnouncementShow;
